import { computed, onUnmounted, ref, watch } from 'vue'
import { getBuyFlowController } from '@/features/crosschain-buy/buy-flow/controller'
import { useQuotes } from '@/features/crosschain-buy/quotes'
import type { CrosschainBuyViewState } from '@/features/crosschain-buy/buy-flow/view-state'
import {
  COMPOSITE_ROUTE_CONFIG,
  PROVIDER_ROUTE_CONFIG,
} from '@/features/crosschain-buy/buy-flow/presentation/route-config'
import { BridgeProvider, ChainId, FlowStatus } from '@/features/crosschain-buy/domain/flow'
import { findStep } from '@/features/crosschain-buy/domain/progress'
import { fromSmallestUnits, TOKEN_DECIMALS } from '@/features/crosschain-buy/domain/amounts'
import {
  TON_TRANSACTION_EXPLORER_URL,
  TRANSACTION_EXPLORER_URL_BY_CHAIN_ID,
} from '@/features/crosschain-buy/shared/transaction-explorers'
import { getStrategy } from '../../presentation/routes'
import { computeStepStatuses } from '../../presentation/routes/step-statuses'
import type { RouteStep, StepStatus, TxLink } from '../../presentation/routes/types'

export interface RouteStepperProps {
  state: CrosschainBuyViewState
  sourceAmount: string
  routeUxStatus?:
    | 'empty'
    | 'invalid_amount'
    | 'invalidated'
    | 'loading'
    | 'ready_preview'
    | 'ready_executable'
    | 'success'
    | 'processing'
    | 'error_recoverable'
    | 'error_blocking'
}

const placeholderStep = (label: string, amountLine: string, amountLoading: boolean): RouteStep => ({
  key: 'placeholder',
  label,
  amountLine,
  amountLoading,
  status: 'pending',
  txLinks: [],
  etaHint: null,
})

const stepCircleClass = (status: StepStatus): string => {
  switch (status) {
    case 'pending':
      return 'bg-gray-100 text-gray-400'
    case 'active':
      return 'bg-blue-50 text-[#1980e6]'
    case 'completed':
      return 'bg-green-50 text-[#16a34a]'
    case 'failed':
      return 'bg-red-50 text-[#b42318]'
  }
}

const formatEta = (eta: number): string => {
  if (eta <= 60) return '< 1 min'
  if (eta <= 120) return '~2 min'
  if (eta <= 300) return '~5 min'
  if (eta <= 900) return '~5-15 min'
  return '~20 min'
}

export const useRouteStepperPresenter = (props: RouteStepperProps) => {
  const controller = getBuyFlowController()
  const quotes = useQuotes()
  const bridgeStep = computed(() => findStep(props.state, 'bridge'))
  const usdtToTonStep = computed(() => findStep(props.state, 'usdt_to_ton'))
  const tonToSpottyStep = computed(() => findStep(props.state, 'ton_to_spotty'))
  const jupiterStep = computed(() => findStep(props.state, 'jupiter_swap'))
  const presentation = computed(() => quotes.presentRoute(props.state.quote))
  const isSuccess = computed(() => props.state.status === FlowStatus.SUCCESS)
  const isRangoFlow = computed(() => props.state.quote?.provider === BridgeProvider.RANGO)
  const isLayerswapFlow = computed(() => props.state.quote?.provider === BridgeProvider.LAYERSWAP)

  const stepStatuses = computed(() =>
    computeStepStatuses(
      props.state.status,
      props.state.lastError,
      props.state.tonLegSubStatus,
      props.state.quote?.provider,
      usdtToTonStep.value?.txHash ?? null,
      usdtToTonStep.value?.status === 'submitted' || usdtToTonStep.value?.status === 'reconciling'
        ? 'active'
        : (usdtToTonStep.value?.status ?? null),
      props.state.quote?.toToken,
      bridgeStep.value?.status ?? null
    )
  )

  watch(
    stepStatuses,
    (next) => {
      const usdtToTonHash = usdtToTonStep.value?.txHash ?? null
      controller.recordDiagnostic('routeStepper.statuses', {
        status: props.state.status,
        tonLegSubStatus: props.state.tonLegSubStatus,
        tonLegUsdtToTonTxHash: usdtToTonHash ? usdtToTonHash.slice(0, 12) : null,
        step1: next.step1,
        step2: next.step2,
        step3: next.step3,
      })
    },
    { immediate: true }
  )

  const authoritativeTonAmount = computed(() => {
    if (usdtToTonStep.value?.amountOut) return usdtToTonStep.value.amountOut
    const askUnits = usdtToTonStep.value?.askUnits
    if (!askUnits) return ''
    try {
      return fromSmallestUnits(askUnits, TOKEN_DECIMALS.TON)
    } catch {
      return ''
    }
  })

  const showSummary = computed(
    () => props.state.status === FlowStatus.QUOTE_READY || props.state.status === FlowStatus.IDLE
  )
  const routeLabel = computed(() => {
    if (presentation.value.isCompositeRoute) return 'Route: Solana → EVM → TON (Symbiosis)'
    if (isRangoFlow.value) return 'Route: Rango'
    if (isLayerswapFlow.value) return 'Route: Layerswap'
    return 'Route: Symbiosis'
  })
  const formattedEta = computed(() => formatEta(props.state.quote?.etaSeconds ?? 0))
  const txCount = computed(() => {
    if (presentation.value.isCompositeRoute) {
      const jupiterCount = presentation.value.skipJupiterSwap ? 0 : 1
      return COMPOSITE_ROUTE_CONFIG.txCount + jupiterCount + 2
    }
    const provider = props.state.quote?.provider
    const config = provider ? PROVIDER_ROUTE_CONFIG[provider] : null
    if (!config) return 0
    const jupiterCount =
      config.extraTxIfJupiter && !presentation.value.skipJupiterSwap ? config.extraTxIfJupiter : 0
    const usdtToTonCount =
      provider === BridgeProvider.LAYERSWAP && props.state.quote?.toToken !== 'TON' ? 1 : 0
    return config.txCount + jupiterCount + usdtToTonCount
  })

  const bridgeStartedAt = ref<number | null>(null)
  let clockAcquired = false
  const bridgeDelayClockActive = computed(() => {
    if (stepStatuses.value.step1 !== 'active') return false
    if (!isLayerswapFlow.value || presentation.value.skipJupiterSwap) return true
    return jupiterStep.value?.status === 'completed' || !!bridgeStep.value?.txHash
  })
  watch(
    bridgeDelayClockActive,
    (isActive) => {
      bridgeStartedAt.value = isActive ? Date.now() : null
      if (isActive === clockAcquired) return
      clockAcquired = isActive
      if (isActive) controller.acquireClock5s()
      else controller.releaseClock5s()
    },
    { immediate: true }
  )
  onUnmounted(() => {
    if (clockAcquired) controller.releaseClock5s()
  })

  const bridgeElapsedSec = computed(() =>
    bridgeStartedAt.value === null
      ? 0
      : Math.floor((controller.clock5s.value - bridgeStartedAt.value) / 1000)
  )
  const bridgeEtaHint = computed(() => {
    const provider = props.state.quote?.provider
    let config = provider ? PROVIDER_ROUTE_CONFIG[provider] : null
    if (presentation.value.isCompositeRoute) config = COMPOSITE_ROUTE_CONFIG
    if (!config) return ''
    return config.etaHintDelayed &&
      config.delayThresholdSec &&
      bridgeElapsedSec.value >= config.delayThresholdSec
      ? config.etaHintDelayed
      : config.etaHint
  })

  const explorerUrl = (base: string | undefined, hash?: string | null): string | null =>
    base && hash ? `${base}${hash}` : null
  const bridgeTxUrl = computed(() => {
    const chainId = props.state.quoteContext?.fromChainId ?? props.state.evm.chainId
    return chainId
      ? explorerUrl(TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[chainId], bridgeStep.value?.txHash)
      : null
  })
  const bridgeDepositTxUrl = computed(() =>
    explorerUrl(TON_TRANSACTION_EXPLORER_URL, bridgeStep.value?.depositTxHash)
  )
  const tonUsdtToTonTxUrl = computed(() =>
    explorerUrl(TON_TRANSACTION_EXPLORER_URL, usdtToTonStep.value?.txHash)
  )
  const tonTxUrl = computed(() =>
    explorerUrl(TON_TRANSACTION_EXPLORER_URL, tonToSpottyStep.value?.txHash)
  )
  const jupiterTxUrl = computed(() =>
    explorerUrl(TRANSACTION_EXPLORER_URL_BY_CHAIN_ID[ChainId.SOLANA], jupiterStep.value?.txHash)
  )
  const bridgeTxLinks = computed<TxLink[]>(() => {
    if (stepStatuses.value.step1 === 'pending') return []
    return [
      bridgeTxUrl.value ? { url: bridgeTxUrl.value, label: 'Source tx' } : null,
      bridgeDepositTxUrl.value ? { url: bridgeDepositTxUrl.value, label: 'Deposit tx' } : null,
    ].filter((link): link is TxLink => link !== null)
  })

  const routeSteps = computed<RouteStep[]>(() => {
    if (props.routeUxStatus === 'empty') {
      return [
        placeholderStep(
          'Enter amount to see route',
          'Select amount and token to calculate route',
          false
        ),
      ]
    }
    if (props.routeUxStatus === 'invalid_amount') {
      return [
        placeholderStep(
          'Adjust amount to see route',
          'Finish or change the amount above to calculate a route',
          false
        ),
      ]
    }
    if (props.routeUxStatus === 'invalidated') {
      return [placeholderStep('Updating route...', 'Recalculating bridge and swap route', true)]
    }
    if (props.routeUxStatus === 'loading') {
      return [placeholderStep('Finding best route...', 'Calculating bridge and swap route', true)]
    }
    if (!props.state.quote && props.state.status === FlowStatus.QUOTE_LOADING) {
      return [placeholderStep('Getting route...', 'Calculating bridge and swap route', true)]
    }

    const strategy = getStrategy(props.state.quote?.provider, presentation.value.isCompositeRoute)
    if (!strategy) return []
    const preview = props.state.routePreview
    const tonAmount = authoritativeTonAmount.value || (preview?.tonAmount ?? '')
    return strategy.compute({
      state: props.state,
      sourceAmount: props.sourceAmount,
      fromToken: props.state.fromToken || '?',
      bridgeOutputAmount: props.state.quote?.toAmount ?? '',
      bridgeOutputToken: props.state.quote?.toToken === 'USDT' ? 'USDT' : 'TON',
      bridgeOutputUsd: props.state.quote?.amountOutUsd ?? '',
      previewLoading: tonAmount ? false : (preview?.isLoading ?? false),
      tonAmount,
      spottyAmount: props.state.spottyAmount || (preview?.spottyAmount ?? ''),
      step1Status: stepStatuses.value.step1,
      step2Status: stepStatuses.value.step2,
      step3Status: stepStatuses.value.step3,
      bridgeTxLinks: bridgeTxLinks.value,
      bridgeEtaHint: bridgeEtaHint.value,
      tonUsdtToTonTxUrl: tonUsdtToTonTxUrl.value,
      tonTxUrl: tonTxUrl.value,
      jupiterTxUrl: jupiterTxUrl.value,
      jupiterUsdtOutput: presentation.value.jupiterUsdtOutput,
      skipJupiterSwap: presentation.value.skipJupiterSwap,
      rangoJupiterPreStep: presentation.value.rangoJupiterPreStep,
      rangoSolAmount: presentation.value.rangoSolAmount,
      intermediateEvmChainId: presentation.value.intermediateEvmChainId,
      evmUsdtAmount: presentation.value.evmUsdtAmount,
    })
  })

  const bottomLine = computed(() => {
    if (isSuccess.value && props.state.spottyAmount) {
      return { label: 'SPOTTY received:', value: props.state.spottyAmount }
    }
    const spotty = props.state.routePreview?.spottyAmount
    return spotty ? { label: 'Expected SPOTTY:', value: `~${spotty}` } : null
  })

  return {
    bottomLine,
    formattedEta,
    isSuccess,
    routeLabel,
    routeSteps,
    showSummary,
    stepCircleClass,
    txCount,
  }
}
